import * as Sentry from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

/**
 * initSentry — wire up Sentry's React SDK if VITE_SENTRY_DSN is set.
 *
 * No-op when the DSN is empty so local dev + preview builds don't ship
 * noise to the project. Tracing + Replay are enabled at conservative
 * sample rates; tune via env if needed.
 *
 * The router instrumentation is BrowserTracingIntegration with the
 * react-router v6 helpers — this captures route transitions as named
 * transactions so we can spot slow pages from the dashboard.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn || dsn.length === 0) {
    // eslint-disable-next-line no-console
    console.info('[sentry] VITE_SENTRY_DSN not set — error reporting disabled.');
    return;
  }

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    (import.meta.env.MODE === 'production' ? 'production' : 'development');

  const release =
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ?? undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        // Customer screens may contain order numbers + addresses; mask
        // text by default and don't record inputs. Network bodies stay
        // off — Supabase JWTs would otherwise leak via headers.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],
    // Trace sampling — start low, raise once we see the data volume.
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    // Replay sampling — only capture replays around errors.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Don't ship development-mode 'ResizeObserver loop limit' noise.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event) {
      // Strip Supabase + Service-Fusion bearer tokens from breadcrumbs
      // and request headers if they ever sneak in.
      if (event.request?.headers) {
        const h = event.request.headers as Record<string, string>;
        if (h.Authorization) h.Authorization = '[redacted]';
        if (h.authorization) h.authorization = '[redacted]';
      }
      return event;
    },
  });
}

/** Re-export the React error boundary so callers don't import @sentry/react directly. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
